import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { TimedSettlementBudgetSummary } from './TimedSettlementBudgetSummary';
import type {
  TimedSettlementFinancialTotals,
  MemberDto,
  PaginatedMembersDto,
} from '@pokerroom/types';
import { api } from '../../lib/api.ts';
import { useTranslation } from 'react-i18next';
import { SettlementSteps, SettlementSummary, settlementStep } from './timedSettlementView';

interface ParticipantInput {
  memberNo: string;
  raCount: number;
  activityParticipant: boolean;
}

interface DraftInput {
  paperReference: string;
  rules: {
    startingChips: number;
    raChips: number;
    prizeContribution: number;
    activityFee1: number;
    zCustomerLevelCode: string;
  };
  participants: ParticipantInput[];
}

interface ParticipantResult {
  memberNo: string;
  nickname?: string;
  displayName?: string;
  memberLevel?: { code: string; displayName: string };
  seat: string | null;
  tableNo?: number | null;
  seatNo?: number | null;
  activityParticipant?: boolean;
  rank: number;
  purchasedChips: number;
  finalChips: number;
  netChips: number;
  baseAward: number;
  rankBonus: number;
  totalAward: number;
  zPoints?: number;
  prize?: number | null;
  participationPoints?: number | null;
  rankPoints?: number | null;
  zipaCredits?: number | null;
  note?: string;
}

interface Preview {
  calculationRevision?: number;
  tournamentName?: string;
  rules?: { settlementLevel?: { name: string } | null };
  totals: TimedSettlementFinancialTotals & {
    participants: number;
    raCount: number;
    units: number;
    expectedChips: number;
    countedChips: number;
    chipDifference: number;
    missingCounts?: number;
    expectedPieces?: Record<string, number> | null;
    countedPieces?: Record<string, number> | null;
    denominationMismatch?: boolean;
  };
  participants: ParticipantResult[];
  denominationException?: { reason: string; actorId: string | null; acceptedAt: string };
}

interface SettlementResponse {
  tournamentName: string;
  phase: 'PREPARATION' | 'COUNTING';
  identities: Record<string, { displayName: string; phoneSuffix: string | null }>;
  levels: Array<{ code: string; displayName: string; isZCustomer?: boolean }>;
  settlementLevels: Array<{
    id: string;
    name: string;
    startingChips: number;
    initialCounts: Record<string, number>;
    raCounts: Record<string, number>;
    raChips: number;
    activityFee1: number;
    prizeContribution: number;
    participationPoints: number;
    rankPoints: number[];
  }>;
  settlementLevel: {
    id: string;
    name: string;
    startingChips: number;
    raChips: number;
    activityFee1: number;
    prizeContribution: number;
    initialCounts: Record<string, number>;
    raCounts: Record<string, number>;
    denominations: number[];
    zCustomerLevelCode?: string;
    sourceLevel?: { name: string };
  } | null;
  settlementLevelVersion: number;
  draft: (DraftInput & { version: number }) | null;
  preview: Preview | null;
  previewStale?: boolean;
  confirmed: {
    version: number;
    confirmedAt: string;
    preview: Preview;
    payload: DraftInput;
  } | null;
}

const emptyParticipant = (): ParticipantInput => ({
  memberNo: '',
  raCount: 0,
  activityParticipant: true,
});

const emptyDraft: DraftInput = {
  paperReference: '',
  rules: {
    startingChips: 0,
    raChips: 0,
    prizeContribution: 0,
    activityFee1: 0,
    zCustomerLevelCode: '',
  },
  participants: [],
};

export function defaultZCustomerLevelCode(
  currentCode: string,
  snapshotCode: string | undefined,
  levels: Array<{ code: string; isZCustomer?: boolean }>,
): string {
  return currentCode || snapshotCode || levels.find((level) => level.isZCustomer)?.code || '';
}

export function savedDraftRefreshInput(draft: DraftInput & { version: number }) {
  return {
    paperReference: draft.paperReference,
    rules: draft.rules,
    participants: draft.participants,
    expectedVersion: draft.version,
  };
}

export function autoRefreshKey({
  id,
  draftVersion,
  previewStale,
  dirty,
  overrideDirty,
  receiptVersion,
  attemptedKey,
  confirmed = false,
}: {
  id: string | undefined;
  draftVersion: number | undefined;
  previewStale: boolean | undefined;
  dirty: boolean;
  overrideDirty: boolean;
  receiptVersion: string;
  attemptedKey: string | null;
  confirmed?: boolean;
}) {
  if (
    !id ||
    draftVersion === undefined ||
    !previewStale ||
    dirty ||
    overrideDirty ||
    receiptVersion ||
    confirmed
  )
    return null;
  const key = `${id}:${draftVersion}`;
  return key === attemptedKey ? null : key;
}

function errorMessage(error: unknown): string | null {
  if (isAxiosError(error)) {
    const validation = error.response?.data?.errors?.validation;
    if (Array.isArray(validation) && validation.every((item) => typeof item === 'string')) {
      return validation.join('；');
    }
    const message = error.response?.data?.message;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join('；');
  }
  if (error instanceof Error) return error.message;
  return null;
}

function currency(value: number | undefined): string {
  return typeof value === 'number' ? value.toLocaleString('zh-TW') : '—';
}

const FIELD = 'mt-1 h-11 w-full border border-base-300 bg-base-100 p-2';
const NUMBER_FIELD = `${FIELD} text-right tabular-nums`;
const ACTION =
  'inline-flex min-h-11 items-center justify-center px-4 py-2 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = `${ACTION} bg-primary text-primary-content hover:bg-primary/90`;
const OUTLINE = `${ACTION} border border-base-300 text-sm text-base-content hover:bg-base-300`;
const DANGER = `${ACTION} bg-error text-error-content hover:bg-error/90`;
const NOTICE = 'border border-warning p-2 text-sm text-base-content';
const CELL = 'px-2 py-2';
const NUM = 'px-2 py-2 text-right tabular-nums';
const HEAD = 'px-2 py-2 text-left';
const HEAD_NUM = 'px-2 py-2 text-right';

export default function TimedSettlementPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('tournament');
  const describeError = (failure: unknown) =>
    errorMessage(failure) ?? t('settlementPage.genericError');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DraftInput>(emptyDraft);
  const autoRefreshAttempt = useRef<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [receiptVersion, setReceiptVersion] = useState('');
  const [selectedLevelId, setSelectedLevelId] = useState('');
  const [levelReason, setLevelReason] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('');
  const [memberIdentities, setMemberIdentities] = useState<
    Record<string, { displayName: string; phoneSuffix: string | null }>
  >({});
  const [resetReason, setResetReason] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideDirty, setOverrideDirty] = useState(false);
  const [overrideRules, setOverrideRules] = useState<DraftInput['rules']>(emptyDraft.rules);
  const [initialCounts, setInitialCounts] = useState<Record<string, number>>({});
  const [raCounts, setRaCounts] = useState<Record<string, number>>({});
  const { data, isLoading, error } = useQuery({
    queryKey: ['timed-settlement', id, receiptVersion],
    queryFn: () =>
      api.get<SettlementResponse>(
        `/tournament/${id}/timed-settlement${receiptVersion ? `?version=${receiptVersion}` : ''}`,
      ),
    enabled: !!id,
    refetchInterval: 5000,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedMemberSearch(memberSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [memberSearch]);

  const {
    data: memberSearchData,
    isFetching: isSearchingMembers,
    isError: memberSearchFailed,
  } = useQuery<PaginatedMembersDto>({
    queryKey: ['timed-settlement-member-search', debouncedMemberSearch],
    queryFn: () => {
      const params = new URLSearchParams({
        page: '1',
        limit: '10',
        search: debouncedMemberSearch,
        status: 'ACTIVE',
      });
      return api.get<PaginatedMembersDto>(`/members?${params}`);
    },
    enabled: debouncedMemberSearch.length >= 1,
    staleTime: 2_000,
  });

  useEffect(() => {
    if (data?.identities) setMemberIdentities((current) => ({ ...current, ...data.identities }));
    if (data?.draft && !dirty) {
      setForm({
        paperReference: data.draft.paperReference,
        rules: data.draft.rules,
        participants: data.draft.participants,
      });
    } else if (data?.settlementLevel && !data.draft && !dirty) {
      setForm((current) => ({
        ...current,
        paperReference:
          current.paperReference ||
          `${new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())} ${data.tournamentName}`,
        rules: {
          ...current.rules,
          startingChips: data.settlementLevel!.startingChips,
          raChips: data.settlementLevel!.raChips,
          prizeContribution: data.settlementLevel!.prizeContribution,
          activityFee1: data.settlementLevel!.activityFee1,
          zCustomerLevelCode: defaultZCustomerLevelCode(
            current.rules.zCustomerLevelCode,
            data.settlementLevel!.zCustomerLevelCode,
            data.levels,
          ),
        },
      }));
    }
    if (!dirty && data?.settlementLevel) setSelectedLevelId(data.settlementLevel.id);
    if (data?.settlementLevel && !overrideDirty) {
      setInitialCounts(data.settlementLevel.initialCounts);
      setRaCounts(data.settlementLevel.raCounts);
      setOverrideRules(
        data.draft?.rules ?? {
          ...emptyDraft.rules,
          startingChips: data.settlementLevel.startingChips,
          raChips: data.settlementLevel.raChips,
          prizeContribution: data.settlementLevel.prizeContribution,
          activityFee1: data.settlementLevel.activityFee1,
          zCustomerLevelCode: defaultZCustomerLevelCode(
            '',
            data.settlementLevel.zCustomerLevelCode,
            data.levels,
          ),
        },
      );
    }
  }, [
    data?.draft,
    data?.settlementLevel,
    data?.identities,
    data?.tournamentName,
    data?.levels,
    dirty,
    overrideDirty,
  ]);

  const changeLevel = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${id}/timed-settlement/level`, {
        levelId: selectedLevelId,
        reason: levelReason.trim(),
        expectedLevelVersion: data?.settlementLevelVersion ?? 0,
        expectedDraftVersion: data?.draft?.version ?? 0,
      }),
    onSuccess: async () => {
      setDirty(false);
      setLevelReason('');
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
      await queryClient.invalidateQueries({ queryKey: ['timed-table-counts', id] });
    },
  });

  const applyOverride = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${id}/timed-settlement/override`, {
        rules: overrideRules,
        initialCounts,
        raCounts,
        reason: overrideReason.trim(),
        expectedLevelVersion: data?.settlementLevelVersion ?? 0,
        expectedDraftVersion: data?.draft?.version ?? 0,
      }),
    onSuccess: async () => {
      setOverrideDirty(false);
      setOverrideReason('');
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
      await queryClient.invalidateQueries({ queryKey: ['timed-table-counts', id] });
    },
  });

  const reset = useMutation({
    mutationFn: () =>
      api.post(`/tournament/${id}/timed-settlement/reset`, {
        reason: resetReason.trim(),
        expectedVersion: data?.draft?.version,
      }),
    onSuccess: async () => {
      setDirty(false);
      setResetReason('');
      setExceptionReason('');
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
      await queryClient.invalidateQueries({ queryKey: ['timed-table-counts', id] });
    },
  });

  const acceptException = useMutation({
    mutationFn: () =>
      api.post(`/tournament/${id}/timed-settlement/denomination-exception`, {
        reason: exceptionReason.trim(),
        expectedVersion: data?.draft?.version,
      }),
    onSuccess: async () => {
      setExceptionReason('');
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.put<SettlementResponse>(`/tournament/${id}/timed-settlement/draft`, {
        ...form,
        expectedVersion: data?.draft?.version ?? 0,
      }),
    onSuccess: async () => {
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
    },
  });
  const refreshPreview = useMutation({
    mutationFn: (draft: DraftInput & { version: number }) =>
      api.put<SettlementResponse>(
        `/tournament/${id}/timed-settlement/draft`,
        savedDraftRefreshInput(draft),
      ),
    onSuccess: async (result) => {
      // A moved seat can remain stale until it is recounted. Do not refresh
      // the version created by this refresh again automatically.
      if (result.draft) autoRefreshAttempt.current = `${id}:${result.draft.version}`;
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
    },
  });
  useEffect(() => {
    if (!data?.draft || save.isPending || save.error || refreshPreview.isPending) return;
    const attemptKey = autoRefreshKey({
      id,
      draftVersion: data.draft.version,
      previewStale: data.previewStale,
      dirty,
      overrideDirty,
      receiptVersion,
      confirmed: !!data?.confirmed,
      attemptedKey: autoRefreshAttempt.current,
    });
    if (!attemptKey) return;
    autoRefreshAttempt.current = attemptKey;
    refreshPreview.mutate(data.draft);
  }, [
    id,
    data?.draft,
    data?.previewStale,
    data?.confirmed,
    dirty,
    overrideDirty,
    receiptVersion,
    save.isPending,
    save.error,
    refreshPreview,
  ]);
  const confirm = useMutation({
    mutationFn: () =>
      api.post<SettlementResponse>(`/tournament/${id}/timed-settlement/confirm`, {
        expectedVersion: data?.draft?.version,
        ...(data?.confirmed ? { reason: correctionReason.trim() } : {}),
      }),
    onSuccess: async () => {
      setCorrectionReason('');
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
    },
  });

  const changeRule = (key: keyof DraftInput['rules'], value: number) => {
    setForm((current) => ({ ...current, rules: { ...current.rules, [key]: value } }));
    setDirty(true);
  };
  const changeParticipant = (index: number, change: Partial<ParticipantInput>) => {
    setForm((current) => ({
      ...current,
      participants: current.participants.map((item, position) =>
        position === index ? { ...item, ...change } : item,
      ),
    }));
    setDirty(true);
  };
  const addParticipant = (member: MemberDto) => {
    if (form.participants.some((participant) => participant.memberNo === member.memberNo)) return;
    setForm((current) => ({
      ...current,
      participants: [...current.participants, { ...emptyParticipant(), memberNo: member.memberNo }],
    }));
    setMemberIdentities((current) => ({
      ...current,
      [member.memberNo]: {
        displayName: member.nickname?.trim() || member.realName?.trim() || member.memberNo,
        phoneSuffix: member.phone?.replace(/\D/g, '').slice(-6) || null,
      },
    }));
    setMemberSearch('');
    setDebouncedMemberSearch('');
    setDirty(true);
  };
  const preview = data?.preview;
  const confirmed = data?.confirmed;
  const memberSearchReady =
    debouncedMemberSearch.length > 0 && debouncedMemberSearch === memberSearch.trim();
  const canConfirm =
    !!data?.draft &&
    data.phase === 'COUNTING' &&
    !!preview &&
    preview.totals.chipDifference === 0 &&
    preview.totals.activityFee2 >= 0 &&
    (preview.totals.missingCounts ?? 0) === 0 &&
    (!preview.totals.denominationMismatch || !!preview.denominationException?.reason) &&
    !data?.previewStale &&
    !dirty &&
    !receiptVersion &&
    !save.isPending &&
    !save.error &&
    !refreshPreview.isPending &&
    !confirm.isPending;

  const guide = settlementStep({
    hasDraft: !!data?.draft,
    dirty: dirty || overrideDirty,
    confirmed: !!confirmed,
    hasPreview: !!preview,
    previewStale: !!data?.previewStale,
    missingCounts: preview?.totals.missingCounts ?? 0,
    chipDifference: preview?.totals.chipDifference ?? 0,
    denominationBlocked: !!preview?.totals.denominationMismatch && !preview?.denominationException,
    counting: data?.phase === 'COUNTING',
    saveFailed: !!save.error,
    awardsExceedPool: (preview?.totals.activityFee2 ?? 0) < 0,
  });
  // One primary action per view: the current step's own button, else the guide's link.
  const savePrimary = guide.step === 1;
  const confirmPrimary = guide.step === 3 && !guide.blocker;
  const stepHeading = (n: 1 | 2 | 3 | 4) => (
    <h2 className="flex items-center gap-2 text-lg font-semibold">
      <span className="inline-flex h-7 w-7 items-center justify-center bg-primary text-sm text-primary-content tabular-nums">
        {n}
      </span>
      {t(`settlementFlow.step${n}`)}
    </h2>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="print:hidden">
        <Link
          to={`/tournament/${id}`}
          className="inline-flex min-h-11 items-center text-sm text-base-content underline underline-offset-4 hover:text-primary"
        >
          {t('settlementPage.back')}
        </Link>
        <h1 className="mt-2 text-2xl font-bold">{t('settlementPage.title')}</h1>
        <p className="mt-1 text-sm text-base-content/70">{t('settlementPage.intro')}</p>
      </div>

      {isLoading && (
        <p role="status" className="text-base-content/70">
          {t('settlementPage.loading')}
        </p>
      )}
      {data && (
        <SettlementSteps
          step={guide.step}
          blocker={guide.blocker}
          emphasis={!savePrimary && !confirmPrimary}
        />
      )}
      {error && (
        <p role="alert" className="text-error">
          {describeError(error)}
        </p>
      )}

      <section
        id="settlement-data"
        className="scroll-mt-4 space-y-4 border border-base-300 p-4 print:hidden"
      >
        {stepHeading(1)}
        <div className="border border-base-300 p-3 space-y-2">
          <label className="block text-sm">
            {t('settlementPage.level')}
            <select
              className={FIELD}
              value={selectedLevelId}
              onChange={(event) => setSelectedLevelId(event.target.value)}
            >
              <option value="">{t('settlementPage.noLevel')}</option>
              {data?.settlementLevel &&
                !data.settlementLevels.some((level) => level.id === data.settlementLevel?.id) && (
                  <option value={data.settlementLevel.id}>
                    {data.settlementLevel.name} · {t('settlementPage.snapshot')}
                  </option>
                )}
              {data?.settlementLevels?.map((level) => (
                <option key={level.id} value={level.id}>
                  {t('settlementPage.levelOption', {
                    name: level.name,
                    starting: level.startingChips.toLocaleString('zh-TW'),
                    ra: level.raChips.toLocaleString('zh-TW'),
                  })}
                </option>
              ))}
            </select>
          </label>
          {selectedLevelId && selectedLevelId !== data?.settlementLevel?.id && (
            <>
              <p className="text-sm">
                {t('settlementPage.levelChangeNotice', {
                  name: data?.settlementLevels.find((level) => level.id === selectedLevelId)?.name,
                })}
              </p>
              <label className="block text-sm">
                {t('settlementPage.levelReason')}
                <input
                  className={FIELD}
                  value={levelReason}
                  onChange={(event) => setLevelReason(event.target.value)}
                />
              </label>
              <button
                type="button"
                className={OUTLINE}
                disabled={Boolean(
                  data?.confirmed ||
                  dirty ||
                  !levelReason.trim() ||
                  changeLevel.isPending ||
                  receiptVersion,
                )}
                onClick={() => changeLevel.mutate()}
              >
                {t('settlementPage.applyLevel')}
              </button>
              {changeLevel.error && (
                <p role="alert" className="text-error text-sm">
                  {describeError(changeLevel.error)}
                </p>
              )}
            </>
          )}
        </div>
        <label className="block text-sm">
          {t('settlementPage.paperReference')}
          <input
            className={FIELD}
            value={form.paperReference}
            onChange={(event) => {
              setForm({ ...form, paperReference: event.target.value });
              setDirty(true);
            }}
          />
        </label>
        <details className="border border-base-300 p-3">
          <summary className="flex min-h-11 cursor-pointer items-center font-medium">
            {t('settlementPage.advancedSummary', {
              starting: form.rules.startingChips.toLocaleString('zh-TW'),
              ra: form.rules.raChips.toLocaleString('zh-TW'),
            })}
          </summary>
          <p className="mt-2 text-sm text-base-content/70">{t('settlementPage.advancedHint')}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {(
              [
                ['startingChips', 'settlementPage.rules.startingChips'],
                ['raChips', 'settlementPage.rules.raChips'],
                ['prizeContribution', 'settlementPage.rules.prizeContributionPerUnit'],
                ['activityFee1', 'settlementPage.rules.activityFee1PerUnit'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="text-sm">
                {t(label)}
                <input
                  type="number"
                  min="0"
                  step="1"
                  readOnly={Boolean(data?.settlementLevel)}
                  className={NUMBER_FIELD}
                  value={form.rules[key]}
                  onChange={(event) => changeRule(key, Number(event.target.value))}
                />
              </label>
            ))}
            <label className="text-sm">
              {t('settlementPage.zCustomerLevel')}
              <select
                className={FIELD}
                value={form.rules.zCustomerLevelCode}
                disabled={Boolean(data?.draft)}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    rules: { ...current.rules, zCustomerLevelCode: event.target.value },
                  }));
                  setDirty(true);
                }}
              >
                <option value="">{t('settlementPage.chooseVenueLevel')}</option>
                {data?.levels
                  .filter((level) => level.isZCustomer)
                  .map((level) => (
                    <option key={level.code} value={level.code}>
                      {level.displayName} ({level.code})
                    </option>
                  ))}
              </select>
            </label>
          </div>
          <p className="mt-2 text-sm text-base-content/70 tabular-nums">
            {t('settlementPage.perUnitFormula', {
              contribution: currency(form.rules.prizeContribution),
              fee: currency(form.rules.activityFee1),
              net: currency(form.rules.prizeContribution - form.rules.activityFee1),
            })}
          </p>
          {data?.settlementLevel && !data.confirmed && (
            <div className="mt-4 space-y-3 border border-base-300 p-3">
              <h3 className="font-medium">{t('settlementPage.overrideTitle')}</h3>
              <p className="text-sm text-base-content/70">{t('settlementPage.overrideHint')}</p>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    ['startingChips', 'settlementPage.rules.startingChips'],
                    ['raChips', 'settlementPage.rules.raChips'],
                    ['prizeContribution', 'settlementPage.rules.prizeContribution'],
                    ['activityFee1', 'settlementPage.rules.activityFee1'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="text-sm">
                    {t(label)}
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className={NUMBER_FIELD}
                      value={overrideRules[key]}
                      onChange={(event) => {
                        setOverrideRules((current) => ({
                          ...current,
                          [key]: Number(event.target.value),
                        }));
                        setOverrideDirty(true);
                      }}
                    />
                  </label>
                ))}
              </div>
              <p className="text-sm text-base-content/70 tabular-nums">
                {t('settlementPage.perUnitFormula', {
                  contribution: currency(overrideRules.prizeContribution),
                  fee: currency(overrideRules.activityFee1),
                  net: currency(overrideRules.prizeContribution - overrideRules.activityFee1),
                })}
              </p>
              <label className="block text-sm">
                {t('settlementPage.zCustomerLevel')}
                <select
                  className={FIELD}
                  value={overrideRules.zCustomerLevelCode}
                  onChange={(event) => {
                    setOverrideRules((current) => ({
                      ...current,
                      zCustomerLevelCode: event.target.value,
                    }));
                    setOverrideDirty(true);
                  }}
                >
                  <option value="">{t('settlementPage.chooseVenueLevel')}</option>
                  {data.levels
                    .filter((level) => level.isZCustomer)
                    .map((level) => (
                      <option key={level.code} value={level.code}>
                        {level.displayName}
                      </option>
                    ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ['initial', initialCounts, setInitialCounts],
                    ['ra', raCounts, setRaCounts],
                  ] as const
                ).map(([kind, values, setter]) => (
                  <fieldset key={kind} className="space-y-2 border border-base-300 p-2">
                    <legend className="text-sm font-medium">
                      {kind === 'initial'
                        ? t('settlementPage.initialPieces')
                        : t('settlementPage.raPieces')}
                    </legend>
                    {data.settlementLevel!.denominations.map((face) => (
                      <label key={face} className="block text-sm tabular-nums">
                        {face.toLocaleString('zh-TW')}
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className={NUMBER_FIELD}
                          value={values[String(face)] ?? 0}
                          onChange={(event) => {
                            setter((current) => ({
                              ...current,
                              [face]: Number(event.target.value),
                            }));
                            setOverrideDirty(true);
                          }}
                        />
                      </label>
                    ))}
                  </fieldset>
                ))}
              </div>
              <label className="block text-sm">
                {t('settlementPage.overrideReason')}
                <input
                  className={FIELD}
                  value={overrideReason}
                  onChange={(event) => setOverrideReason(event.target.value)}
                />
              </label>
              <button
                type="button"
                className={OUTLINE}
                disabled={
                  !overrideDirty || !overrideReason.trim() || dirty || applyOverride.isPending
                }
                onClick={() => applyOverride.mutate()}
              >
                {t('settlementPage.applyOverride')}
              </button>
              {applyOverride.error && (
                <p role="alert" className="text-error text-sm">
                  {describeError(applyOverride.error)}
                </p>
              )}
            </div>
          )}
        </details>
        <div className="space-y-2">
          <label htmlFor="timed-settlement-member-search" className="block text-sm font-medium">
            {t('settlementPage.addParticipant')}
          </label>
          <input
            id="timed-settlement-member-search"
            type="search"
            autoComplete="off"
            aria-controls="timed-settlement-member-results"
            aria-expanded={memberSearch.trim().length > 0}
            placeholder={t('settlementPage.searchPlaceholder')}
            className="h-11 w-full border border-base-300 bg-base-100 p-2 sm:max-w-md"
            value={memberSearch}
            onChange={(event) => setMemberSearch(event.target.value)}
          />
          {memberSearch.trim().length > 0 && (
            <div
              id="timed-settlement-member-results"
              className="relative max-h-64 overflow-y-auto border border-base-300 sm:max-w-2xl"
            >
              <p className="sr-only" role="status" aria-live="polite">
                {!memberSearchReady || isSearchingMembers
                  ? t('settlementPage.searchingStatus')
                  : memberSearchFailed
                    ? t('settlementPage.searchFailedStatus')
                    : t('settlementPage.searchFound', {
                        count: memberSearchData?.data.length ?? 0,
                      })}
              </p>
              {(!memberSearchReady || isSearchingMembers) && (
                <p className="p-3 text-sm text-base-content/70">{t('settlementPage.searching')}</p>
              )}
              {memberSearchReady && memberSearchFailed && (
                <p className="p-3 text-sm text-error">{t('settlementPage.searchFailed')}</p>
              )}
              {memberSearchReady &&
                !isSearchingMembers &&
                !memberSearchFailed &&
                (memberSearchData?.data.length ?? 0) === 0 && (
                  <p className="p-3 text-sm text-base-content/70">
                    {t('settlementPage.searchEmpty')}
                  </p>
                )}
              {memberSearchReady &&
                !isSearchingMembers &&
                !memberSearchFailed &&
                memberSearchData?.data.map((member) => {
                  const alreadyAdded = form.participants.some(
                    (participant) => participant.memberNo === member.memberNo,
                  );
                  return (
                    <button
                      key={member.id}
                      type="button"
                      className="flex min-h-11 w-full items-center justify-between gap-3 border-b border-base-300 px-3 py-2 text-left last:border-b-0 hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={alreadyAdded}
                      onClick={() => addParticipant(member)}
                    >
                      <span>
                        <span className="font-medium">
                          {member.nickname?.trim() || member.realName?.trim() || member.memberNo}
                        </span>
                        <span className="ml-2 text-xs text-base-content/70">
                          #{member.memberNo}
                        </span>
                        {member.phone && (
                          <span className="ml-2 text-xs text-base-content/70">
                            {t('settlementPage.phoneSuffix', {
                              suffix: member.phone.replace(/\D/g, '').slice(-6),
                            })}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs">
                        {alreadyAdded ? t('settlementPage.added') : t('settlementPage.addToEvent')}
                      </span>
                    </button>
                  );
                })}
            </div>
          )}
        </div>
        <div role="group" className="space-y-2" aria-label={t('settlementPage.participants')}>
          {form.participants.length === 0 && (
            <p className="border border-base-300 p-4 text-sm text-base-content/70">
              {t('settlementPage.noParticipants')}
            </p>
          )}
          {form.participants.map((participant, index) => {
            const identity =
              memberIdentities[participant.memberNo] ?? data?.identities?.[participant.memberNo];
            return (
              <div key={participant.memberNo} className="border border-base-300 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{identity?.displayName ?? participant.memberNo}</p>
                    <p className="text-xs text-base-content/70">
                      {participant.memberNo} ·{' '}
                      {t('settlementPage.phoneSuffixLine', {
                        suffix: identity?.phoneSuffix ?? t('settlementPage.notProvided'),
                      })}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="min-h-11 px-3 text-error hover:bg-error/10"
                    onClick={() => {
                      setForm((current) => ({
                        ...current,
                        participants: current.participants.filter(
                          (item) => item.memberNo !== participant.memberNo,
                        ),
                      }));
                      setDirty(true);
                    }}
                  >
                    {t('settlementPage.remove')}
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <label className="text-sm">
                    {t('settlementPage.raCount')}
                    <input
                      aria-label={t('settlementPage.raCountAria', { n: index + 1 })}
                      type="number"
                      inputMode="numeric"
                      min="0"
                      step="1"
                      className="ml-2 h-11 w-20 border border-base-300 bg-base-100 p-2 text-right tabular-nums"
                      value={participant.raCount}
                      onChange={(event) =>
                        changeParticipant(index, { raCount: Number(event.target.value) })
                      }
                    />
                  </label>
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      aria-label={t('settlementPage.activityAria', { n: index + 1 })}
                      checked={participant.activityParticipant}
                      onChange={(event) =>
                        changeParticipant(index, { activityParticipant: event.target.checked })
                      }
                    />
                    {t('settlementPage.activity')}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            className={savePrimary ? PRIMARY : OUTLINE}
            disabled={
              (!dirty && !data?.draft) ||
              save.isPending ||
              refreshPreview.isPending ||
              overrideDirty ||
              !form.rules.zCustomerLevelCode
            }
            onClick={() => save.mutate()}
          >
            {t('settlementPage.saveAndPreview')}
          </button>
        </div>
        {!form.rules.zCustomerLevelCode && (
          <p role="status" className={NOTICE}>
            {t('settlementPage.needZCustomerLevel')}
          </p>
        )}
        {save.error && (
          <p role="alert" className="text-error">
            {describeError(save.error)}
          </p>
        )}
        {dirty && (
          <p role="status" className={NOTICE}>
            {t('settlementPage.unsaved')}
          </p>
        )}
        {data?.draft && !data.confirmed && (
          <details className="border border-error p-3">
            <summary className="flex min-h-11 cursor-pointer items-center text-error">
              {t('settlementPage.resetSummary')}
            </summary>
            <p className="mt-2 text-sm">{t('settlementPage.resetHint')}</p>
            <label className="mt-2 block text-sm">
              {t('settlementPage.resetReason')}
              <input
                className={FIELD}
                value={resetReason}
                onChange={(event) => setResetReason(event.target.value)}
              />
            </label>
            <button
              type="button"
              className={`mt-2 ${DANGER}`}
              disabled={!resetReason.trim() || dirty || reset.isPending}
              onClick={() => {
                if (window.confirm(t('settlementPage.resetConfirm'))) reset.mutate();
              }}
            >
              {t('settlementPage.reset')}
            </button>
            {reset.error && (
              <p role="alert" className="text-error text-sm">
                {describeError(reset.error)}
              </p>
            )}
          </details>
        )}
      </section>

      <section
        id="settlement-counting"
        className="scroll-mt-4 space-y-3 border border-base-300 p-4 print:hidden"
      >
        {stepHeading(2)}
        {dirty || !data?.draft ? (
          <p className="text-sm">{t('settlementPage.countingBefore')}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/tables" className={OUTLINE}>
              {t('settlementPage.goToTables')}
            </Link>
            <p className="text-sm">{t('settlementPage.countingHint')}</p>
          </div>
        )}
      </section>

      {preview && (
        <section
          id="settlement-preview"
          className="scroll-mt-4 space-y-4 border border-base-300 p-4 print:hidden"
        >
          {stepHeading(3)}
          {data?.previewStale && (
            <div className="space-y-2 border border-warning p-3 text-sm text-base-content">
              <p role="status">
                {refreshPreview.isPending
                  ? t('settlementPage.staleRefreshing')
                  : dirty || overrideDirty
                    ? t('settlementPage.staleSaveFirst')
                    : t('settlementPage.staleNotUpdated')}
              </p>
              {refreshPreview.error && (
                <p role="alert" className="text-error">
                  {describeError(refreshPreview.error)}
                </p>
              )}
              {!dirty && !overrideDirty && !save.isPending && !refreshPreview.isPending && (
                <button
                  type="button"
                  className={OUTLINE}
                  onClick={() => data.draft && refreshPreview.mutate(data.draft)}
                >
                  {t('settlementPage.retryPreview')}
                </button>
              )}
            </div>
          )}
          <div
            role="status"
            className={`border p-4 text-lg font-bold ${dirty || data?.previewStale || save.isPending || save.error || refreshPreview.isPending || preview.totals.missingCounts || preview.totals.chipDifference !== 0 || preview.totals.denominationMismatch ? 'border-error bg-error/15 text-error' : 'border-success bg-success/15 text-success'}`}
          >
            {dirty || data?.previewStale || save.isPending || save.error
              ? t('settlementPage.verdict.needsUpdate')
              : (preview.totals.missingCounts ?? 0) > 0
                ? t('settlementPage.verdict.missingCounts', {
                    count: preview.totals.missingCounts,
                  })
                : preview.totals.chipDifference !== 0
                  ? t('settlementPage.verdict.chipMismatch', {
                      difference: currency(preview.totals.chipDifference),
                    })
                  : preview.totals.denominationMismatch
                    ? preview.denominationException
                      ? t('settlementPage.verdict.piecesRecorded')
                      : t('settlementPage.verdict.piecesNeedReason')
                    : preview.totals.expectedPieces
                      ? t('settlementPage.verdict.donePieces')
                      : t('settlementPage.verdict.doneTotal')}
          </div>
          <p className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            <span>{t('settlementPage.metaTournament', { name: data?.tournamentName })}</span>
            <span>
              {t('settlementPage.metaLevel', {
                name: data?.settlementLevel?.name ?? t('settlementPage.notSelected'),
              })}
            </span>
            <span>
              {t('settlementPage.metaPaper', {
                reference: data?.draft?.paperReference ?? t('settlementPage.notSaved'),
              })}
            </span>
          </p>
          <SettlementSummary
            totals={preview.totals}
            keys={[
              'participants',
              'raCount',
              'units',
              'expectedChips',
              'countedChips',
              'chipDifference',
            ]}
          />
          <TimedSettlementBudgetSummary
            totals={preview.totals}
            calculationRevision={preview.calculationRevision}
          />
          {preview.totals.expectedPieces && preview.totals.countedPieces && (
            <div className="overflow-x-auto border border-base-300">
              <table className="w-full text-sm">
                <caption className="px-2 py-2 text-left font-medium">
                  {t('settlementPage.pieces.caption')}
                </caption>
                <thead>
                  <tr className="border-b border-base-300">
                    <th scope="col" className={HEAD_NUM}>
                      {t('settlementPage.pieces.face')}
                    </th>
                    <th scope="col" className={HEAD_NUM}>
                      {t('settlementPage.pieces.expected')}
                    </th>
                    <th scope="col" className={HEAD_NUM}>
                      {t('settlementPage.pieces.counted')}
                    </th>
                    <th scope="col" className={HEAD_NUM}>
                      {t('settlementPage.pieces.difference')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(preview.totals.expectedPieces).map(([face, expected]) => {
                    const actual = preview.totals.countedPieces?.[face] ?? 0;
                    return (
                      <tr
                        key={face}
                        className={`border-b border-base-300 ${actual !== expected ? 'bg-error/15 text-error' : ''}`}
                      >
                        <td className={NUM}>{Number(face).toLocaleString('zh-TW')}</td>
                        <td className={NUM}>{expected}</td>
                        <td className={NUM}>{actual}</td>
                        <td className={NUM}>{actual - expected}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {preview.totals.denominationMismatch &&
            preview.totals.chipDifference === 0 &&
            (preview.totals.missingCounts ?? 0) === 0 && (
              <div className="space-y-2 border border-error p-3">
                <p className="font-medium text-error">{t('settlementPage.piecesOnlyMismatch')}</p>
                {preview.denominationException ? (
                  <p>
                    {t('settlementPage.exceptionReason', {
                      reason: preview.denominationException.reason,
                    })}
                  </p>
                ) : (
                  <>
                    <label className="block text-sm">
                      {t('settlementPage.exceptionReasonInput')}
                      <input
                        className={FIELD}
                        value={exceptionReason}
                        onChange={(event) => setExceptionReason(event.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className={OUTLINE}
                      disabled={
                        !exceptionReason.trim() ||
                        dirty ||
                        data?.previewStale ||
                        acceptException.isPending
                      }
                      onClick={() => acceptException.mutate()}
                    >
                      {t('settlementPage.recordException')}
                    </button>
                    {acceptException.error && (
                      <p role="alert" className="text-error">
                        {describeError(acceptException.error)}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          <ResultTable participants={preview.participants} />
          {(preview.totals.missingCounts ?? 0) > 0 && (
            <p role="status" className={NOTICE}>
              {t('settlementPage.block.missingCounts', { count: preview.totals.missingCounts })}
            </p>
          )}
          {data?.previewStale && (
            <p role="status" className={NOTICE}>
              {t('settlementPage.block.stale')}
            </p>
          )}
          {preview.totals.chipDifference !== 0 && (
            <p role="status" className={NOTICE}>
              {t('settlementPage.block.chips')}
            </p>
          )}
          {preview.totals.activityFee2 < 0 && (
            <p role="status" className={NOTICE}>
              {t('settlementPage.block.awards')}
            </p>
          )}
          {confirmed && (
            <label className="block text-sm">
              {t('settlementPage.correctionReason')}
              <input
                className={FIELD}
                value={correctionReason}
                onChange={(event) => setCorrectionReason(event.target.value)}
              />
            </label>
          )}
          <button
            type="button"
            className={confirmPrimary ? PRIMARY : OUTLINE}
            disabled={!canConfirm || (!!confirmed && !correctionReason.trim())}
            onClick={() => {
              if (window.confirm(t('settlementPage.confirmPrompt'))) confirm.mutate();
            }}
          >
            {t('settlementPage.confirm')}
          </button>
          {confirm.error && (
            <p role="alert" className="text-error">
              {describeError(confirm.error)}
            </p>
          )}
        </section>
      )}

      {(data?.draft || confirmed || receiptVersion) && (
        <div id="settlement-receipt" className="scroll-mt-4 space-y-3 print:hidden">
          {stepHeading(4)}
          {!confirmed && <p className="text-sm">{t('settlementFlow.receiptPending')}</p>}
          <label className="block text-sm">
            {t('settlementPage.reprintVersion')}
            <input
              type="number"
              min="1"
              step="1"
              className="mt-1 ml-2 h-11 w-24 border border-base-300 bg-base-100 p-2 text-right tabular-nums"
              value={receiptVersion}
              onChange={(event) => setReceiptVersion(event.target.value)}
            />
          </label>
        </div>
      )}
      {confirmed && (
        <section
          className="settlement-receipt space-y-4 border border-base-300 p-4"
          aria-label={t('settlementPage.receiptLabel')}
        >
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold">限時賽結算簽收表</h2>
              <p className="text-sm">
                紙本編號：{confirmed.payload.paperReference}　版本：{confirmed.version}　確認時間：
                {new Date(confirmed.confirmedAt).toLocaleString('zh-TW')}
              </p>
              <p className="text-sm">
                賽事：{confirmed.preview.tournamentName ?? '歷史賽事'}　應有籌碼：
                {currency(confirmed.preview.totals.expectedChips)}
              </p>
            </div>
            <button
              type="button"
              className={`${OUTLINE} print:hidden`}
              onClick={() => window.print()}
            >
              {t('settlementPage.print')}
            </button>
          </div>
          <TimedSettlementBudgetSummary
            totals={confirmed.preview.totals}
            calculationRevision={confirmed.preview.calculationRevision}
          />
          <div className="print:hidden">
            <SettlementSummary
              totals={confirmed.preview.totals}
              keys={['expectedChips', 'countedChips', 'chipDifference']}
            />
          </div>
          {confirmed.preview.totals.expectedPieces && confirmed.preview.totals.countedPieces && (
            <p className="text-sm tabular-nums print:hidden">
              {t('settlementPage.piecesCheck')}
              {Object.entries(confirmed.preview.totals.expectedPieces)
                .map(
                  ([face, expected]) =>
                    `${Number(face).toLocaleString()} ${confirmed.preview.totals.countedPieces?.[face] ?? 0}/${expected}`,
                )
                .join(' · ')}
            </p>
          )}
          {confirmed.preview.denominationException && (
            <p className="border border-base-300 p-2 text-sm print:hidden">
              {t('settlementPage.piecesExceptionReason', {
                reason: confirmed.preview.denominationException.reason,
              })}
            </p>
          )}
          <ResultTable participants={confirmed.preview.participants} signed />
          <p className="text-xs">本表為簽收紀錄；列印不代表獎金支付或點數入帳。</p>
        </section>
      )}
      <style>
        {
          '@media print { @page { size: A4 landscape; margin: 8mm; } body:has(.settlement-receipt) header { display: none !important; } body:has(.settlement-receipt) .zipa-admin-content { border-top: 0 !important; } :is(div, main):has(.settlement-receipt) { display: block !important; height: auto !important; max-height: none !important; overflow: visible !important; } .settlement-receipt { border: 0; padding: 0; margin: 0 !important; } .settlement-receipt > :not(:last-child) { margin-block-end: 6px !important; } .settlement-receipt table { min-width: 0; width: 100%; font-size: 9pt; table-layout: fixed; } .settlement-receipt thead { display: table-header-group; } .settlement-receipt tr { break-inside: avoid; page-break-inside: avoid; } .settlement-receipt td, .settlement-receipt th { padding: 3px 5px; overflow-wrap: anywhere; } .settlement-receipt th:last-child { width: 18%; } }'
        }
      </style>
    </div>
  );
}

function ResultTable({
  participants,
  signed = false,
}: {
  participants: ParticipantResult[];
  signed?: boolean;
}) {
  const { t } = useTranslation('tournament');
  // The signed table is the printed sign sheet; its wording stays zh-TW.
  const label = (key: string, signedText: string) =>
    signed ? signedText : t(`settlementPage.result.${key}`);
  return (
    <div
      {...(signed
        ? {}
        : { role: 'region', 'aria-label': t('settlementPage.result.caption'), tabIndex: 0 })}
      className={signed ? '' : 'overflow-x-auto'}
    >
      <table className={`relative w-full text-sm ${signed ? '' : 'min-w-[900px]'}`}>
        <caption className="sr-only">{label('caption', '簽收明細')}</caption>
        <thead>
          <tr className="border-b border-base-300 print:border-base-content">
            <th scope="col" className={HEAD_NUM}>
              {label('rank', '名次')}
            </th>
            {!signed && (
              <th scope="col" className={HEAD}>
                {t('settlementPage.result.tableSeat')}
              </th>
            )}
            <th scope="col" className={HEAD}>
              {label('member', '姓名／等級')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('chips', '買入／結算／淨籌碼')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('awards', '原始獎金／名次獎金／總獎金')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('points', '活動／參賽點／名次點')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('prize', '實領獎金／Z 點')}
            </th>
            <th scope="col" className={HEAD}>
              {label('note', '備註')}
            </th>
            {signed && (
              <th scope="col" className={HEAD}>
                簽收
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {participants.map((p, index) => (
            <tr key={`${p.memberNo}-${index}`} className="border-b border-base-300 align-top">
              <td className={NUM}>{p.rank}</td>
              {!signed && (
                <td className={CELL}>
                  {p.tableNo != null
                    ? t('settlementPage.result.seat', { table: p.tableNo, seat: p.seatNo ?? '—' })
                    : p.seat?.includes(':')
                      ? t('settlementPage.result.legacySeat')
                      : (p.seat ?? t('settlementPage.result.unassigned'))}
                </td>
              )}
              <td className={CELL}>
                {!signed && (
                  <>
                    {p.memberNo}
                    <br />
                  </>
                )}
                {p.displayName ?? p.nickname ?? '—'}
                <br />
                {p.memberLevel?.displayName ?? '—'}
              </td>
              <td className={NUM}>
                {currency(p.purchasedChips)}
                <br />
                {currency(p.finalChips)}
                <br />
                {currency(p.netChips)}
              </td>
              <td className={NUM}>
                {currency(p.baseAward)}
                <br />
                {currency(p.rankBonus)}
                <br />
                <strong>{currency(p.totalAward)}</strong>
              </td>
              <td className={NUM}>
                {p.activityParticipant === false ? label('no', '否') : label('yes', '是')}
                <br />
                {currency(p.participationPoints ?? undefined)}
                <br />
                {currency(p.rankPoints ?? undefined)}
              </td>
              <td className={NUM}>
                {signed ? (
                  <strong className="underline">{currency(p.prize ?? undefined)}</strong>
                ) : (
                  currency(p.prize ?? undefined)
                )}
                <br />
                {!signed && (
                  <>
                    {currency(p.zipaCredits ?? undefined)}
                    <br />
                  </>
                )}
                {currency(p.zPoints ?? undefined)}
              </td>
              <td className={CELL}>{p.note ?? ''}</td>
              {signed && <td className={`${CELL} h-12`} />}
            </tr>
          ))}
        </tbody>
      </table>
      <p className={`mt-1 text-sm text-base-content/70 ${signed ? 'print:hidden' : ''}`}>
        {t('settlementPage.result.footnote')}
      </p>
    </div>
  );
}
